import { DataProvider } from '@refinedev/core';

import { AxiosInstance } from 'axios';
import Cookies from 'js-cookie';

import {
  axiosInstance,
  generateFilter,
  generatePagination,
  generateSort,
} from '@/utility';
import logger from '@/utility/helper/logger';

const dataProvider = (
  apiUrl: string,
  httpClient: AxiosInstance = axiosInstance,
): Omit<Required<DataProvider>, 'createMany' | 'updateMany' | 'deleteMany'> => ({
  getList: async ({
    resource,
    pagination,
    filters,
    sorters,
  }) => {
    try {
      const url = `${apiUrl}/${resource}`;

      const filter = generateFilter(filters);
      const sort = generateSort(sorters);
      const page = generatePagination(pagination || {});

      const response = await httpClient.post(url, {
        filter,
        sort,
        pagination: page,
      });

      return {
        data: response?.data?.data,
        // total: parseInt(response.headers['x-total-count'], 10),
        total: response?.data?.pagination?.total_rows || 0,
      };
    } catch (error: any) {
      // Check if the error is an instance of Error
      if (error instanceof Error) {
        logger(
          'ERROR',
          error.message || 'Error',
          `${apiUrl}/${resource}`,
          'userId',
          'webApp',
          'getList',
        );
      } else {
        logger(
          'ERROR',
          'An unexpected error occurred',
          `${apiUrl}/${resource}`,
          'userId',
          'webApp',
          'getList',
        );
      }

      return Promise.reject(error);
    }
  },

  getOne: async ({ resource, id }) => {
    try {
      const response = await httpClient.get(`${apiUrl}/${resource}/${id}`);

      return { data: response?.data?.data || response.data };
    } catch (error: any) {
      // Check if the error is an instance of Error
      if (error instanceof Error) {
        logger(
          'ERROR',
          error.message || 'Error',
          `${apiUrl}/${resource}`,
          'userId',
          'webApp',
          'getOne',
        );
      } else {
        logger(
          'ERROR',
          'An unexpected error occurred',
          `${apiUrl}/${resource}`,
          'userId',
          'webApp',
          'getOne',
        );
      }

      return Promise.reject(error);
    }
  },

  create: async ({ resource, variables, meta }) => {
    const headers = meta?.headers ?? {};
    try {
      const userLogin = Cookies.get('user_login');
      const { tenantId = '', userId = '' } = userLogin ? JSON.parse(userLogin) : {};
      const response = await httpClient.post(
        `${apiUrl}/${resource}`,
        {
          ...variables,
          tenant_id: tenantId,
          actor_user_id: userId,
        },
        { headers },
      );

      logger(
        'INFO',
        response.data.message,
        `${apiUrl}/${resource}`,
        'userId',
        'webApp',
        'create',
      );

      return { data: response.data };
    } catch (error: any) {
      // Check if the error is an instance of Error
      if (error instanceof Error) {
        logger(
          'ERROR',
          error.message || 'Error',
          `${apiUrl}/${resource}`,
          'userId',
          'webApp',
          'create',
        );
      } else {
        logger(
          'ERROR',
          'An unexpected error occurred',
          `${apiUrl}/${resource}`,
          'userId',
          'webApp',
          'create',
        );
      }

      return Promise.reject(error);
    }
  },

  update: async ({ resource, id, variables }) => {
    const updatedResource = resource.replace(':id', id.toString());

    try {
      const userLogin = Cookies.get('user_login');
      const { tenantId = '', userId = '' } = userLogin ? JSON.parse(userLogin) : {};
      const response = await httpClient.put(`${apiUrl}/${updatedResource}`, {
        ...variables,
        tenant_id: tenantId,
        actor_user_id: userId,
      });

      logger(
        'INFO',
        response.data.message,
        `${apiUrl}/${resource}`,
        'userId',
        'webApp',
        'update',
      );

      return { data: response.data?.data || response.data };
    } catch (error: any) {
      // Check if the error is an instance of Error
      if (error instanceof Error) {
        logger(
          'ERROR',
          error.message || 'Error',
          `${apiUrl}/${resource}`,
          'userId',
          'webApp',
          'update',
        );
      } else {
        logger(
          'ERROR',
          'An unexpected error occurred',
          `${apiUrl}/${resource}`,
          'userId',
          'webApp',
          'update',
        );
      }

      return Promise.reject(error);
    }
  },

  deleteOne: async ({ resource, id, variables }) => {
    const updatedResource = resource.replace(':id', id.toString());
    const { tenantId, userId } = JSON.parse(Cookies.get('user_login') as string);

    try {
      const response = await httpClient.delete(`${apiUrl}/${updatedResource}`, {
        data: {
          ...variables,
          tenant_id: tenantId,
          actor_user_id: userId,
        },
      });

      logger(
        'INFO',
        response.data.message,
        `${apiUrl}/${resource}`,
        'userId',
        'webApp',
        'deleteOne',
      );

      return { data: response.data };
    } catch (error: any) {
      // Check if the error is an instance of Error
      if (error instanceof Error) {
        logger(
          'ERROR',
          error.message || 'Error',
          `${apiUrl}/${resource}`,
          'userId',
          'webApp',
          'deleteOne',
        );
      } else {
        logger(
          'ERROR',
          'An unexpected error occurred',
          `${apiUrl}/${resource}`,
          'userId',
          'webApp',
          'deleteOne',
        );
      }

      return Promise.reject(error);
    }
  },

  getMany: async ({ resource, ids }) => {
    try {
      const response = await httpClient.get(`${apiUrl}/${resource}`, {
        params: { ids: ids.join(',') },
      });

      return { data: response.data };
    } catch (error: any) {
      // Check if the error is an instance of Error
      if (error instanceof Error) {
        logger(
          'ERROR',
          error.message || 'Error',
          `${apiUrl}/${resource}`,
          'userId',
          'webApp',
          'getMany',
        );
      } else {
        logger(
          'ERROR',
          'An unexpected error occurred',
          `${apiUrl}/${resource}`,
          'userId',
          'webApp',
          'getMany',
        );
      }

      return Promise.reject(error);
    }
  },

  getApiUrl: () => apiUrl,

  custom: async ({
    url,
    method,
    payload,
    headers,
  }) => {
    try {
      let axiosResponse;
      switch (method) {
        case 'put':
        case 'post':
        case 'patch':
          axiosResponse = await httpClient[method](
            url,
            payload,
            {
              headers: { ...(headers || {}) },
            },
          );
          break;
        case 'delete':
          axiosResponse = await httpClient.delete(
            url,
            {
              data: payload,
              headers: { ...(headers || {}) },
            },
          );
          break;
        default:
          axiosResponse = await httpClient.get(url, { headers: { ...(headers || {}) } });
          break;
      }

      const response = axiosResponse;

      return await Promise.resolve({ data: response.data });
    } catch (error: any) {
      // Check if the error is an instance of Error
      if (error instanceof Error) {
        logger(
          'ERROR',
          error.message || 'Error',
          url,
          'userId',
          'webApp',
          'custom',
        );
      } else {
        logger(
          'ERROR',
          'An unexpected error occurred',
          url,
          'userId',
          'webApp',
          'custom',
        );
      }

      return Promise.reject(error);
    }
  },
});

export default dataProvider;
